import { useRef, useState } from "react";
import { Loader2, Upload, CheckCircle2, MapPin, Clock, FileText, X } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { fieldCls, Label, errText } from "@/components/SupportTicketForm";

const JOBS = [
  {
    id: "book-design-specialist", title: "Book Design Specialist",
    summary: "Shape how every KLYR book looks, from cover systems to interior typography that holds up in print.",
    duties: [
      "Design cover templates, type pairings and layout rules that our studio applies to thousands of books",
      "Review generated covers and interiors, and turn what you see into clear, fixable design rules",
      "Keep print files KDP-ready: trim, bleed, spine and safe-zone details",
    ],
    skills: ["A portfolio of published book covers or interiors", "Strong typography and colour sense", "Comfort with print production (InDesign, Affinity or similar)"],
  },
  {
    id: "website-engineer", title: "Website Engineer",
    summary: "Build the product authors use every day: fast, reliable and genuinely pleasant to work in.",
    duties: [
      "Ship features across our React front end and Python (FastAPI) back end",
      "Improve speed, reliability and the book-creation experience end to end",
      "Work closely with design on polished, accessible interfaces",
    ],
    skills: ["Professional experience with React and a modern back-end stack", "Care for performance, testing and clean code", "Bonus: PDF, image or print tooling experience"],
  },
];

const Job = ({ job, onApply }) => (
  <article data-testid={`job-${job.id}`} className="rounded-2xl border border-gold-500/15 bg-white/[0.02] p-6 sm:p-7 hover:border-gold-300/40 transition-colors">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 className="font-display text-2xl text-slate-100">{job.title}</h3>
        <div className="mt-2 flex flex-wrap gap-3 font-ui text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Remote</span>
          <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Contract</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Flexible hours</span>
        </div>
      </div>
      <button data-testid={`job-apply-${job.id}`} onClick={() => onApply(job.id)} className="rounded-full border border-gold-300/50 text-gold-100 hover:bg-gold-300 hover:text-stone-950 font-ui text-sm font-semibold px-5 py-2 transition-colors">Apply</button>
    </div>
    <p className="mt-4 font-read text-slate-300 leading-relaxed">{job.summary}</p>
    <div className="mt-5 grid sm:grid-cols-2 gap-6">
      {[["What you'll do", job.duties], ["What you bring", job.skills]].map(([h, items]) => (
        <div key={h}>
          <h4 className="font-ui text-sm font-semibold text-slate-200 mb-2">{h}</h4>
          <ul className="space-y-1.5">{items.map((t) => <li key={t} className="flex gap-2.5 font-read text-sm text-slate-400 leading-relaxed"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold-300 shrink-0" />{t}</li>)}</ul>
        </div>
      ))}
    </div>
  </article>
);

const ResumePicker = ({ file, setFile, setErr }) => {
  const ref = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(pdf|docx?|DOCX?|PDF)$/.test(f.name)) return setErr("Please choose a PDF or Word document.");
    if (f.size > 10 * 1024 * 1024) return setErr("Resume must be 10 MB or smaller.");
    setErr(""); setFile(f);
  };
  return (
    <div>
      <Label>Resume (PDF or Word, up to 10 MB)</Label>
      <input ref={ref} data-testid="careers-resume-input" type="file" accept=".pdf,.doc,.docx" onChange={pick} className="hidden" />
      {file ? (
        <div data-testid="careers-resume-selected" className="flex items-center justify-between rounded-xl border border-gold-500/20 bg-white/[0.04] px-4 py-3">
          <span className="font-ui text-sm text-slate-200 truncate">{file.name} <span className="text-slate-500">({Math.ceil(file.size / 1024)} KB)</span></span>
          <button type="button" data-testid="careers-resume-remove" onClick={() => setFile(null)} className="text-slate-400 hover:text-slate-100"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" data-testid="careers-resume-upload-button" onClick={() => ref.current?.click()} className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-gold-500/30 hover:border-gold-300/60 bg-white/[0.02] px-4 py-6 font-ui text-sm text-slate-300 transition-colors">
          <Upload className="w-4 h-4" /> Upload your resume
        </button>
      )}
    </div>
  );
};

const EMPTY = { name: "", email: "", portfolio_url: "", message: "", website: "" };

const ApplyForm = ({ role, setRole, formRef }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setErr("Please upload your resume.");
    setBusy(true); setErr(""); setPct(0);
    const fd = new FormData();
    Object.entries({ ...f, role }).forEach(([k, v]) => fd.append(k, v));
    fd.append("resume", file);
    try {
      const { data } = await axios.post(`${API}/careers/apply`, fd, { onUploadProgress: (p) => p.total && setPct(Math.round((p.loaded / p.total) * 100)) });
      setDone(data.reference); setF(EMPTY); setFile(null);
    } catch (e2) {
      setErr(errText(e2, "Upload failed. Please try again."));
    } finally { setBusy(false); }
  };
  if (done) return (
    <div ref={formRef} data-testid="careers-apply-success" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] p-6">
      <CheckCircle2 className="w-6 h-6 text-emerald-300 mb-3" />
      <h3 className="font-ui text-lg font-semibold text-slate-100">Application sent</h3>
      <p className="font-read text-slate-300 mt-2">Thank you. Your reference is <strong data-testid="careers-apply-reference" className="text-gold-200">{done}</strong>. We read every application and will be in touch by email.</p>
    </div>
  );
  return (
    <form ref={formRef} data-testid="careers-apply-form" onSubmit={submit} className="rounded-2xl border border-gold-500/15 bg-white/[0.02] p-6 sm:p-8 space-y-5">
      <h3 className="font-display text-2xl text-slate-100">Apply now</h3>
      <div><Label htmlFor="c-role">Role</Label>
        <select id="c-role" data-testid="careers-role-select" value={role} onChange={(e) => setRole(e.target.value)} className={fieldCls}>
          {JOBS.map((j) => <option key={j.id} value={j.id} className="bg-navy-900">{j.title}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><Label htmlFor="c-name">Full name</Label><input id="c-name" data-testid="careers-name-input" required maxLength={120} value={f.name} onChange={set("name")} className={fieldCls} /></div>
        <div><Label htmlFor="c-email">Email</Label><input id="c-email" data-testid="careers-email-input" type="email" required value={f.email} onChange={set("email")} className={fieldCls} /></div>
      </div>
      <div><Label htmlFor="c-port">Portfolio or LinkedIn (optional)</Label><input id="c-port" data-testid="careers-portfolio-input" type="url" maxLength={300} placeholder="https://" value={f.portfolio_url} onChange={set("portfolio_url")} className={fieldCls} /></div>
      <ResumePicker file={file} setFile={setFile} setErr={setErr} />
      <div><Label htmlFor="c-msg">Anything you'd like us to know? (optional)</Label><textarea id="c-msg" data-testid="careers-message-input" rows={4} maxLength={4000} value={f.message} onChange={set("message")} className={fieldCls} /></div>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} className="hidden" name="website" />
      {err && <p data-testid="careers-apply-error" className="font-ui text-sm text-red-300">{err}</p>}
      <button data-testid="careers-apply-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gold-300 hover:bg-gold-200 text-stone-950 font-ui font-semibold text-sm px-6 py-3 transition-colors disabled:opacity-60">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {pct}%</> : "Send application"}
      </button>
    </form>
  );
};

export const CareersBoard = () => {
  const [role, setRole] = useState(JOBS[0].id);
  const formRef = useRef(null);
  const apply = (id) => { setRole(id); formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  return (
    <div data-testid="careers-board" className="mt-12 max-w-3xl space-y-6">
      <h2 className="font-ui text-lg font-semibold text-slate-100">Open roles</h2>
      {JOBS.map((j) => <Job key={j.id} job={j} onApply={apply} />)}
      <div className="pt-4"><ApplyForm role={role} setRole={setRole} formRef={formRef} /></div>
    </div>
  );
};
